import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { LayoutComponent } from '../../core/layout/layout.component';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { StationaryService } from '../../features/stationary/services/stationary.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ToastrService } from 'ngx-toastr';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-stationary-items',
  standalone: true,
  imports: [CommonModule, MatInputModule, AgGridAngular, FormsModule],
  templateUrl: './stationary-items.component.html',
  styleUrl: './stationary-items.component.scss',
})
export class StationaryItemsComponent implements OnInit {
  public viewPortSize: boolean = false;
  public loading: boolean = true;
  readonly dialog = inject(MatDialog);
  private stationaryService = inject(StationaryService);
  private router = inject(Router);
  // private toastr: ToastrService;
  private toastr = inject(ToastrService);

  columnDefs: any[] = [];
  // public rowData: any[] | null = null;
  public rowData: any[] = [];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10];
  public themeClass: string = 'ag-theme-quartz';

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  canCreateStationaryItems$ = this.permissionService.hasPermission(
    ModuleIds.StationaryItems,
    'Create'
  );
  canEditStationaryItems$ = this.permissionService.hasPermission(
    ModuleIds.StationaryItems,
    'Edit'
  );
  canDeleteStationaryItems$ = this.permissionService.hasPermission(
    ModuleIds.StationaryItems,
    'Delete'
  );
  canExpenseStationaryItems$ = this.permissionService.hasPermission(
    ModuleIds.StationaryItems,
    'Expense'
  );
  canExpenseLogsStationaryItems$ = this.permissionService.hasPermission(
    ModuleIds.StationaryItems,
    'Expense Logs'
  );
  canDeleteStationaryItems:boolean = false;
  canEditStationaryItems:boolean = false;
  canExpenseLogsStationaryItems:boolean = false;
  canExpenseStationaryItems:boolean = false;

  constructor(private permissionService: PermissionService) {}

  ngOnInit(): void {
    this.checkViewportSize();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchStationaries(searchTerm);
      });
    this.fetchStationaries();
    this.canEditStationaryItems$.subscribe(val => {
      this.canEditStationaryItems = val;
    });
    this.canDeleteStationaryItems$.subscribe(val => {
      this.canDeleteStationaryItems = val;
    });
    this.canExpenseLogsStationaryItems$.subscribe(val => {
      this.canExpenseLogsStationaryItems = val;
    });
    this.canExpenseStationaryItems$.subscribe(val => {
      this.canExpenseStationaryItems = val;
    });
  }

  checkViewportSize() {
    this.viewPortSize = window.innerWidth > 1200;
    this.updateColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkViewportSize();
  }

  fetchStationaries(search = '') {
    this.stationaryService.fetchStationary(search).subscribe({
      next: (data) => {
        this.rowData = data.map((stationary: any) => ({
          id: stationary?.id,
          itemName: stationary?.stationoryName || '--',
          code: stationary?.stationoryCode || '--',
          stock: stationary?.stock || '--',
          currentPrice:
            `AED ${parseFloat(stationary?.price).toFixed(2)}` || '--',
        }));

        this.paginationPageSizeSelector =
          PaginationHelper.getPaginationPageSizeOptions(this.rowData);
      },
      error: (error) => {
        console.error('Error fetching stationaries:', error);
      },
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onCellClicked(params: any) {
    const cellData = params.data;
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;
      console.log('action', action);

      if (action === 'edit') {
        this.router.navigate([
          `stationary-items/stationary-edit/${cellData?.id}`,
        ]);
      } else if (action === 'delete') {
        this.onDelete(cellData);
      } 
    } else if(this.canExpenseStationaryItems || this.canExpenseLogsStationaryItems){
      this.router.navigate([`stationary-items/expense-logs/${cellData?.id}`]);
    }
  }

  onDelete(data: any) {
    const stationaryId = data.id;
    if (!stationaryId) return;
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete Stationary ?`,
        confirmText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.stationaryService.deleteStationaryById(stationaryId).subscribe({
          next: () => {
            this.toastr.success('Stationary deleted successfully!', 'Success');
            this.fetchStationaries();
          },
          error: (error) => {
            this.toastr.error(`Failed to delete Stationary`);
            console.error('Deletion error:', error);
          },
        });
      }
    });
  }

  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Item Name',
        field: 'itemName',
        flex: this.viewPortSize ? 1 : 0,
      },
      { headerName: 'Code', field: 'code', flex: this.viewPortSize ? 1 : 0 },
      { headerName: 'Stock', field: 'stock', flex: this.viewPortSize ? 1 : 0 },
      {
        headerName: 'Price',
        field: 'currentPrice',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];

    this.columnDefs.push({
      field: 'action',
      headerName: 'Actions',
      floatingFilter: false,
      filter: false,
      flex: this.viewPortSize ? 1 : 0,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.gap = '20px';

        const editIcon = document.createElement('img');
        editIcon.src = 'assets/images/icons/icon-edit.svg';
        editIcon.style.cursor = 'pointer';
        editIcon.setAttribute('data-action', 'edit');
        if(this.canEditStationaryItems){
        container.appendChild(editIcon);
        }
        const deleteIcon = document.createElement('img');
        deleteIcon.src = 'assets/images/icons/icon-delete.svg';
        deleteIcon.style.cursor = 'pointer';
        deleteIcon.setAttribute('data-action', 'delete');
        if(this.canDeleteStationaryItems){
        container.appendChild(deleteIcon);
        }
        return container;
      },
    });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }
}
